import { GridHelperService } from './grid-helper-service';
import { ArrayHelperService } from './array-helper-service';

// <summary>
// The default column configuration for a grid is stored as an array of objects and can typically be found in the
// default-column-configuration.js file. The user's custom column configuration is returned to the frontend as a json blob.
// This function will check if the object passed in is a string and convert it to an array of objects if needed.
// </summary>
export const parseColumnConfiguration = (columnConfiguration) => {
  if (typeof columnConfiguration === 'string') {
    return JSON.parse(columnConfiguration || '[]');
  }
  return columnConfiguration;
};

// <summary>
// Given an array of column configurations, this function will move the column FROM index A TO index B.
// Primary Usage - When the user "drags and drops" a column to a new position in the grid, this function will reorder the
// columns and store the changes in the user's custom column configuration.
// </summary>
// <param name="columnConfiguration">Array of column configurations</param>
// <param name="moveFromIdx">Index of the column to move</param>
// <param name="moveToIdx">Index where the column should be moved</param>
// <returns>an array of columns that will be displayed on the grid in the new order</returns>
export const reorderGridColumns = (columnConfiguration, moveFromIdx, moveToIdx) => {
  const arr = parseColumnConfiguration(columnConfiguration);
  const { nonPinnedColumns, pinnedColumns } = GridHelperService.getColumnsInfo(arr);

  // this will move the item to the correct place in the array but it doesn't update the index property
  const sortedColumns = ArrayHelperService.reorder(nonPinnedColumns, moveFromIdx, moveToIdx);

  // this puts the column configuration back into the correct order with the pinned columns at the beginning
  const newColumns = [...pinnedColumns, ...sortedColumns];

  // this updates the index property on each column configuration to match the items location in the array
  const newColumnsNames = newColumns.map((column) => column.id);
  const newColumnState = newColumns.map((column) => ({
    ...column,
    index: newColumnsNames.indexOf(column.id),
  }));

  return newColumnState;
};

// <summary>
// checks that the named column exists in the column configuration
// Example Column Configuration Object:
// {
//   id: 'AliasSKUs',
//   mappedId: 'alias',
//   width: 125,
//   index: 0,
//   pinned: false,
//   nonsortable: true,
//   minWidth: 70,
// }
// </summary>
// <param name="columnId">this is a string that represents the name (id) of the column to check for</param>
// <param name="columnConfiguration">an array or string of column configurations</param>
// <returns>true if the column exists in the column configuration, false otherwise</returns>
export const isColumnInColumnConfiguration = (columnId, columnConfiguration) => {
  const arr = parseColumnConfiguration(columnConfiguration);
  return arr.some((column) => column.id === columnId);
};

// <summary>
// Inserts a column from the grid's default column configuration into the user's custom column configuration.
// Example Column Configuration Object:
// {
//   id: 'AliasSKUs',
//   mappedId: 'alias',
//   width: 125,
//   index: 0,
//   pinned: false,
//   nonsortable: true,
//   minWidth: 70,
// }
// </summary>
// <param name="columnConfiguration">an array or string of column configurations, typically, the user's custom column configuration
// since that is what needs to be updated. But this could be any array of column configurations.</param>
// <param name="columnToInsertColumnConfig">single object that represents the column to be inserted.
// NOTE* The index property of the this object will be used to place the column in the correct location in the array</param>
// <returns>an array of columns that will be displayed on the grid with the new column added</returns>
export const insertColumn = (columnToInsertColumnConfig, columnConfiguration) => {
  const arr = parseColumnConfiguration(columnConfiguration);
  const { nonPinnedColumns, pinnedColumns } = GridHelperService.getColumnsInfo(arr);

  // if the column is pinned or already in the nonpinned columns (in case this was not checked),
  // then we don't need to insert or reorder the columns
  if (
    isColumnInColumnConfiguration(columnToInsertColumnConfig.id, pinnedColumns) ||
    isColumnInColumnConfiguration(columnToInsertColumnConfig.id, nonPinnedColumns)
  ) {
    return arr;
  }

  // insert the column into the end of the array
  arr.push(columnToInsertColumnConfig);
  const sourceIdx = nonPinnedColumns.map((col) => col.id).indexOf(columnToInsertColumnConfig.id);

  // if the we want to move the column to a spot in the array that is greater than the total length of the array,
  // then we don't need to move the column
  if (sourceIdx <= columnToInsertColumnConfig.index) {
    return arr;
  }

  const source = sourceIdx;
  const destination = columnToInsertColumnConfig.index;

  return reorderGridColumns(arr, source, destination);
};
